import React, {useCallback, useMemo} from 'react';
import {FormatDateOptions, useIntl} from 'react-intl';
import {Button, BUTTON_VARIANT, SPACE, Text} from '@lookiero/aurora';
import {AccountApplication} from '@application/Account';
import {AuthApplication} from '@application/Auth';
import {eventManager} from '@application/Event';
import {UserEvent} from '@domain/model/Event';
import {useOverallInfo, useToast} from '@components/_context';
import {MountableModal} from '@components/molecules';
import {MODALITY, Subscription} from '@domain/model/Account/Subscription/Subscription';
import {getDaysDiff, toAvailableDate} from '../../../../../helpers';

import {useCalendarDates} from '../../_hooks/useCalendarDates';
import {DATE_FORMAT, SKIP_MONTH, TEXT} from './ModalOrderSkip.definition';
import {useLoading} from '@views/Account/Plan/_hooks/useLoading';

export type ModalOrderSkipProps = {
  isVisible: boolean;
  onClose: () => void;
  onSuccess: () => Promise<void>;
  date: Date;
};

const getNextDateAvailable = (date: Date, months: number, day = 1, unavailableDates: Date[] = []): Date => {
  const newDate = new Date(date.getFullYear(), date.getMonth() + months, day);
  return toAvailableDate(newDate, unavailableDates);
};

export const ModalOrderSkip: React.FC<ModalOrderSkipProps> = ({isVisible, onClose, onSuccess, date: currentDate}) => {
  const {showSuccess, showError} = useToast();
  const {formatDate, formatMessage} = useIntl();
  const {executeSkipMonth, subscription, isInterimOrder, userNextBoxDate} = useOverallInfo();
  const {isLoading, payload: calendarDates} = useCalendarDates();
  const {lkDay, modality} = subscription as unknown as Subscription;
  const {loading, startLoading, stopLoading} = useLoading();

  const parseLocaleDate = useCallback(
    (date: Date | undefined): string | undefined => {
      return date && formatDate(date, DATE_FORMAT as FormatDateOptions);
    },
    [formatDate],
  );

  const estimationDate = useMemo(() => {
    if (isLoading) return;
    if (isInterimOrder) {
      const baseDate = userNextBoxDate ? new Date(userNextBoxDate) : currentDate;
      return toAvailableDate(baseDate, calendarDates && calendarDates.unavailableDates);
    }
    const monthsToSkip = SKIP_MONTH[modality || MODALITY.ON_DEMAND];
    return getNextDateAvailable(currentDate, monthsToSkip, lkDay, calendarDates && calendarDates.unavailableDates);
  }, [isLoading, isInterimOrder, userNextBoxDate, modality, currentDate, lkDay, calendarDates]);

  const handleSkipOrder = async (): Promise<boolean> => {
    try {
      await AccountApplication.skipMonth();

      const {id: userId} = await AuthApplication.getLoginInfo();
      const daysSkipped = Math.abs(getDaysDiff(currentDate, estimationDate));
      eventManager.emit(UserEvent.ORDER_SKIPPED, {userId, promoCode: '', daysSkipped});

      showSuccess({
        message: formatMessage(TEXT.TOAST_SUCCESS, {date: parseLocaleDate(estimationDate)}),
      });
      return true;
    } catch {
      showError({message: formatMessage(TEXT.TOAST_ERROR)});
      return false;
    }
  };

  const handleSubmit = async (): Promise<void> => {
    try {
      startLoading();

      const success = await handleSkipOrder();

      if (success) {
        executeSkipMonth();
        await onSuccess();
        onClose();
      }
    } finally {
      stopLoading();
    }
  };

  return (
    <MountableModal
      portalID="modal-order-skip"
      isVisible={isVisible}
      title={formatMessage(TEXT.MODAL_TITLE)}
      description={formatMessage(TEXT.MODAL_DESCRIPTION, {
        date: <Text>{parseLocaleDate(estimationDate)}</Text>,
      })}
      onClose={onClose}
    >
      <Button busy={loading} onPress={handleSubmit}>
        {formatMessage(TEXT.MODAL_CTA)}
      </Button>
      <Button onPress={onClose} marginTop={SPACE.SPACE_4} variant={BUTTON_VARIANT.SECONDARY}>
        {formatMessage(TEXT.MODAL_CTA_CANCEL)}
      </Button>
    </MountableModal>
  );
};
